/// <amd-module name="Wiki/Editor/WikiEditorStore" />
import { Action } from "VSS/Reflux/Action";
import { Store } from "VSS/Reflux/Store";

import {
    AttachmentsAddedPayload,
    IEditorStore,
    IWikiEditorState,
    PageContentUpdatedPayload,
    PageNameUpdatedPayload,
    PageSelectionUpdatePayload
} from "Wiki/Editor/Contracts";
import { ActionListener } from "Wiki/ViewCommon/ActionListener";
import { Attachment, ErrorState, IEditorLegacyModules, OperationStatus } from "Wiki/ViewCommon/Contracts";

export class EditorActions {
    public pageSaveStarted = new Action<boolean>();
    public updateSavePageStatus = new Action<OperationStatus>();
    public pageSaveFailed = new Action<ErrorState>();
    public pageContentUpdated = new Action<PageContentUpdatedPayload>();
    public pageNameUpdated = new Action<PageNameUpdatedPayload>();
    public pageSelectionUpdated = new Action<PageSelectionUpdatePayload>();
    public pageNameErrorUpdated = new Action<string | undefined>();
    public errorMessageUpdated = new Action<string | undefined>();
    public attachmentsAdded = new Action<AttachmentsAddedPayload>();
    public attachmentRead = new Action<Attachment>();
    public clearAttachments = new Action<string[]>();
    public previewContentUpdated = new Action<string>();
    public pasteAsHtmlToggled = new Action<void>();
}

export function registerActionsWithStores(actionListener: ActionListener, actions: EditorActions, store: EditorStore): void {
    actionListener.addListener(actions.pageSaveStarted, store.pageSaveStarted);
    actionListener.addListener(actions.updateSavePageStatus, store.updateSavePageStatus);
    actionListener.addListener(actions.pageSaveFailed, store.pageSaveFailed);
    actionListener.addListener(actions.pageContentUpdated, store.updatePageContent);
    actionListener.addListener(actions.pageNameUpdated, store.updatePageName);
    actionListener.addListener(actions.pageNameErrorUpdated, store.updatePageNameError);
    actionListener.addListener(actions.errorMessageUpdated, store.updateErrorMessage);
    actionListener.addListener(actions.attachmentsAdded, store.addAttachments);
    actionListener.addListener(actions.attachmentRead, store.updateAttachment);
    actionListener.addListener(actions.clearAttachments, store.clearAttachments);
    actionListener.addListener(actions.previewContentUpdated, store.updatePreviewContent);
    actionListener.addListener(actions.pageSelectionUpdated, store.updatePageSelection);
    actionListener.addListener(actions.pasteAsHtmlToggled, store.togglePasteAsHtmlFlag);
}

export class EditorStore extends Store implements IEditorStore {
    private _state: IWikiEditorState;

    constructor(changedEvent: string, initialName: string, private _legacyScripts: IEditorLegacyModules, hideCloseButton?: boolean) {
        super(changedEvent);
        this._state = this._getInitialState(initialName, hideCloseButton);
    }

    public getState = (): IWikiEditorState => {
        return this._state;
    };

    public initializeTranslatedContent = (translatedContent: string): void => {
        this._state.pageContent = translatedContent;
        this._state.previewContent = translatedContent;
        this.emitChanged();
    };

    public resetState = (name: string, hideCloseButton?: boolean): void => {
        this._clearAttachments();
        this._state = this._getInitialState(name, hideCloseButton);
        this.emitChanged();
    };

    public updatePageContent = (pageContentUpdatedPayload: PageContentUpdatedPayload): void => {
        this._state.pageContent = pageContentUpdatedPayload.content;
        this._state.selectionStart = pageContentUpdatedPayload.selectionStart;
        this._state.selectionEnd = pageContentUpdatedPayload.selectionEnd;
        this.emitChanged();
    };

    public updatePageName = (pageNameUpdatedPayload: PageNameUpdatedPayload): void => {
        this._state.pageName = pageNameUpdatedPayload.pageName;
        this._state.pageNameError = pageNameUpdatedPayload.pageNameError;
        this.emitChanged();
    };

    public updatePageNameError = (pageNameError?: string): void => {
        this._state.pageNameError = pageNameError;
        this.emitChanged();
    };

    public updateErrorMessage = (errorMessage?: string): void => {
        this._state.errorState = { message: errorMessage };
        this.emitChanged();
    };

    public updatePageSelection = (pageSelectionUpdateLoad: PageSelectionUpdatePayload): void => {
        this._state.selectionStart = pageSelectionUpdateLoad.selectionStart;
        this._state.selectionEnd = pageSelectionUpdateLoad.selectionEnd;
        this.emitChanged();
    };

    public pageSaveStarted = (closeEditorAfterSave: boolean): void => {
        this._state.savePageStatus = OperationStatus.InProgress;
        this._state.closeEditorAfterSave = closeEditorAfterSave;
        this.emitChanged();
    };

    public pageSaveFailed = (errorState: ErrorState): void => {
        this._state.savePageStatus = OperationStatus.Failed;
        this._state.errorState = errorState;
        this.emitChanged();
    };

    public updateSavePageStatus = (savePageStatus: OperationStatus): void => {
        this._state.savePageStatus = savePageStatus;
        if (savePageStatus === OperationStatus.Completed) {
            this._state.hideCloseButton = false;
            this._state.errorState = undefined;
        }
        this.emitChanged();
    };

    public clearAttachments = (attachmentNames: string[]): void => {
        for (const fileName of attachmentNames) {
            const attachment = this._state.attachments[fileName];
            if (attachment) {
                this._clearAttachment(attachment);

                this._state.totalSizeOfAttachments -= attachment.file.size;
                delete this._state.attachments[fileName];
            }
        }
        this.emitChanged();
    };

    public addAttachments = (attachmentsPayload: AttachmentsAddedPayload): void => {
        attachmentsPayload.attachments.forEach((value: Attachment) => {
            const serverFriendlyAttachmentName: string = value.file.guidSuffixedFileName;
            this._state.attachments[serverFriendlyAttachmentName] = value;
            this._state.totalSizeOfAttachments += value.file.size;
        });
        this.emitChanged();
    };

    public updateAttachment = (attachment: Attachment): void => {
        const serverFriendlyAttachmentName = attachment.file.guidSuffixedFileName;
        this._state.attachments[serverFriendlyAttachmentName] = attachment;
        this.emitChanged();
    };

    public updatePreviewContent = (content: string): void => {
        this._state.previewContent = content;
        this.emitChanged();
    };

    public togglePasteAsHtmlFlag = (): void => {
        this._state.shouldPasteAsHtml = !this._state.shouldPasteAsHtml;
        this.emitChanged();
    };

    private _clearAttachments(): void {
        for (const fileName of Object.keys(this._state.attachments)) {
            const attachment = this._state.attachments[fileName];
            this._clearAttachment(attachment);
        }

        this._state.attachments = {};
        this._state.totalSizeOfAttachments = 0;
    }

    private _clearAttachment(attachment: Attachment): void {
        URL.revokeObjectURL(attachment.objectUrl);

        const file = attachment.file;
        if ((file as any).msClose) {
            (file as any).msClose();
        }
    }

    private _getInitialState = (name: string, hideCloseButton?: boolean): IWikiEditorState => {
        return {
            pageContent: undefined,
            previewContent: undefined,
            pageName: name,
            pageNameError: undefined,
            savePageStatus: OperationStatus.NotStarted,
            closeEditorAfterSave: false,
            selectionStart: 0,
            selectionEnd: 0,
            shouldPasteAsHtml: false,
            attachments: {},
            totalSizeOfAttachments: 0,
            legacyScripts: this._legacyScripts,
            hideCloseButton: hideCloseButton
        };
    };
}
